/** @jsx jsx */

import { ReactNode, isValidElement } from 'react';

import { Icon } from '@zapier/zinnia-icons';
import { Colors, Text } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

const iconMap = {
  infoErrors: {
    name: 'formXCircle' as const,
    color: 'StatusError',
  },
  infoWarnings: {
    name: 'alertCircleColor' as const,
    color: undefined,
  },
  infoActivationTasks: {
    name: 'alertTriangleFill' as const,
    color: 'UiPrimaryStrongest',
  },
  error: {
    name: 'formXCircle' as const,
    color: 'StatusError' as const,
  },
  warning: {
    name: 'alertCircleColor' as const,
    color: undefined,
  },
};

const textColor = {
  infoErrors: 'StatusError' as const,
  infoWarnings: 'UiPrimaryStrongest' as const,
  infoActivationTasks: 'UiPrimaryStrongest' as const,
  error: 'StatusError' as const,
  warning: 'StatusWarning' as const,
};

export enum FieldType {
  warning = 'warning',
  error = 'error',
  infoErrors = 'infoErrors',
  infoWarnings = 'infoWarnings',
  infoActivationTasks = 'infoActivationTasks',
}

const Styles = {
  root: type => [
    css`
      display: flex;
      margin-top: 10px;
      align-items: center;
    `,
    type === FieldType.warning
      ? css`
          margin-top: 10px;
          border: 1px solid ${Colors.StatusErrorWeaker};
          border-radius: 10px;
          background-color: ${Colors.StatusErrorWeaker};
          align-items: center;
          padding: 10px;
        `
      : (type === FieldType.infoActivationTasks ||
          type === FieldType.infoWarnings) &&
        css`
          color: ${Colors.UiPrimaryStrongest};
        `,
  ],
};

type TypedProps = {
  message: ReactNode;
  fieldKey: string;
  type: FieldType;
};

type Props = {
  message: ReactNode;
  fieldKey: string;
};

const defaultProps = {
  fieldKey: 'default',
};

const FieldMessage = ({ message, fieldKey, type }: TypedProps) => {
  // Prevent an empty message container if empty string
  if (!message) {
    return null;
  }

  if (isValidElement(message)) {
    return message;
  }

  return (
    <span css={Styles.root(type)} key={`${fieldKey}-${type}`}>
      <Icon color={iconMap[type].color} name={iconMap[type].name} size={20} />
      <Text color={textColor[type]} fontWeight={600}>
        {message}
      </Text>
    </span>
  );
};

const FieldWarningMessage = ({ message, fieldKey }: Props) => {
  const props = {
    message,
    fieldKey,
    type: FieldType.infoWarnings,
  };
  return <FieldMessage {...props} />;
};

const FieldErrorMessage = ({ message, fieldKey }: Props) => {
  const props = {
    message,
    fieldKey,
    type: FieldType.infoErrors,
  };
  return <FieldMessage {...props} />;
};

FieldMessage.defaultProps = defaultProps;
FieldErrorMessage.defaultProps = defaultProps;
FieldWarningMessage.defaultProps = defaultProps;

export default FieldMessage;
export { FieldWarningMessage, FieldErrorMessage };
